// tippi/src/components/driver/InvoiceProfileForm.js
import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import {
  EMPTY_INVOICE_PROFILE, normalizeInvoiceProfile, validateInvoiceProfile,
} from '../../utils/invoiceProfile';
import { COLORS } from '../../styles/tokens';

// Skjema for sjåførens utbetalings- og fakturaopplysninger (users/{uid}.invoiceProfile).
// Brukes både i profilen (InvoiceProfileCard) og i registreringen (embedded + onSkip).
//   onSaved:  kalles etter vellykket lagring
//   onSkip:   gir en «Hopp over for nå»-knapp med forklaring
//   embedded: kompakt variant for registreringen («Lagre og fortsett»)
//   prefill:  startverdier for tomme felt, f.eks. { legalName, orgNumber } fra registreringen
export default function InvoiceProfileForm({ onSaved, onSkip, embedded = false, prefill }) {
  const { currentUser, userDoc, refreshUserDoc } = useAuth();
  const saved = userDoc?.invoiceProfile;
  const [form, setForm] = useState(() => ({
    ...EMPTY_INVOICE_PROFILE,
    legalName: prefill?.legalName ?? '',
    orgNumber: prefill?.orgNumber ?? '',
    ...(saved ?? {}),
    invoiceAddress: { ...EMPTY_INVOICE_PROFILE.invoiceAddress, ...(saved?.invoiceAddress ?? {}) },
  }));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'

  // Enhver redigering gjør forrige lagre-status foreldet
  const edited = () => setStatus(s => (s === 'saving' ? s : 'idle'));
  const set = (field) => (e) => { edited(); setForm(f => ({ ...f, [field]: e.target.value })); };
  const setAddr = (field) => (e) => { edited(); setForm(f => ({ ...f, invoiceAddress: { ...f.invoiceAddress, [field]: e.target.value } })); };

  async function handleSave() {
    const found = validateInvoiceProfile(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    if (!currentUser) { setStatus('error'); return; }

    const profile = normalizeInvoiceProfile(form);
    const bankChanged = Boolean(saved?.bankAccount) && saved.bankAccount !== profile.bankAccount;
    setStatus('saving');
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        invoiceProfile: profile,
        // Brukes til å varsle om endret kontonummer
        ...(bankChanged ? { invoiceProfileChangedAt: serverTimestamp() } : {}),
      });
    } catch {
      setStatus('error');
      return;
    }
    try {
      await refreshUserDoc();
    } catch {
      // Lagringen lyktes; en mislykket oppfriskning av brukerdokumentet er ikke en lagringsfeil.
    }
    setStatus('saved');
    if (onSaved) onSaved();
  }

  return (
    <div>
      <Field id="inv-name" label="Firmanavn eller navn" value={form.legalName} onChange={set('legalName')} error={errors.legalName} maxLength={120} autoComplete="organization" />
      <Field id="inv-org" label="Organisasjonsnummer" value={form.orgNumber} onChange={set('orgNumber')} error={errors.orgNumber} inputMode="numeric" />
      <label style={styles.check}>
        <input type="checkbox" checked={form.mvaRegistered}
               onChange={e => { edited(); setForm(f => ({ ...f, mvaRegistered: e.target.checked })); }} />
        <span>Jeg er registrert i mva-registeret</span>
      </label>
      <Field id="inv-account" label="Utbetalingskonto (kontonummer)" value={form.bankAccount} onChange={set('bankAccount')} error={errors.bankAccount} inputMode="numeric" />
      <Field id="inv-street" label="Gateadresse" value={form.invoiceAddress.street} onChange={setAddr('street')} error={errors.street} maxLength={120} autoComplete="street-address" />
      <div style={styles.row}>
        <Field id="inv-postal" label="Postnummer" value={form.invoiceAddress.postalCode} onChange={setAddr('postalCode')} error={errors.postalCode} inputMode="numeric" maxLength={4} autoComplete="postal-code" style={{ flex: '0 0 110px' }} />
        <Field id="inv-city" label="Poststed" value={form.invoiceAddress.city} onChange={setAddr('city')} error={errors.city} maxLength={80} autoComplete="address-level2" style={{ flex: 1 }} />
      </div>

      {status === 'error' && <p role="alert" style={styles.error}>Kunne ikke lagre. Prøv igjen.</p>}
      {status === 'saved' && <p role="status" style={styles.saved}>Lagret</p>}
      <button type="button" className="tippi-btn" style={styles.save} onClick={handleSave} disabled={status === 'saving'}>
        {status === 'saving' ? 'Lagrer…' : embedded ? 'Lagre og fortsett' : 'Lagre'}
      </button>
      {onSkip && (
        <>
          <button type="button" className="tippi-btn-secondary" style={styles.skip} onClick={onSkip} disabled={status === 'saving'}>
            Hopp over for nå
          </button>
          <p style={styles.note}>
            Du kan ikke ta eller by på oppdrag før dette er fylt ut. Du finner det senere under Profil.
          </p>
        </>
      )}
    </div>
  );
}

function Field({ id, label, value, onChange, error, inputMode, maxLength, autoComplete, style }) {
  return (
    <div style={{ marginTop: 10, ...style }}>
      <label htmlFor={id} style={styles.label}>{label}</label>
      <input id={id} value={value} onChange={onChange} inputMode={inputMode}
             maxLength={maxLength} autoComplete={autoComplete}
             aria-invalid={!!error} aria-describedby={error ? id + '-err' : undefined}
             style={{ ...styles.input, ...(error ? styles.inputError : {}) }} />
      {error && <div id={id + '-err'} role="alert" style={styles.fieldError}>{error}</div>}
    </div>
  );
}

const styles = {
  label:      { display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 5 },
  input:      { width: '100%', boxSizing: 'border-box', height: 48, padding: '0 14px', borderRadius: 14, border: `1px solid ${COLORS.border}`, fontSize: 15.5, fontFamily: 'inherit', background: COLORS.white, color: COLORS.textDark },
  inputError: { borderColor: COLORS.danger },
  fieldError: { fontSize: 12, color: COLORS.dangerText, marginTop: 4 },
  check:      { display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, marginTop: 12, color: COLORS.textDark },
  row:        { display: 'flex', gap: 10 },
  error:      { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 12px', borderRadius: 10, fontSize: 14, margin: '12px 0 0' },
  saved:      { color: COLORS.successText, fontSize: 14, fontWeight: 600, margin: '12px 0 0' },
  save:       { width: '100%', height: 52, borderRadius: 26, border: 'none', background: COLORS.accent, color: '#1a1a2e', fontSize: 16, fontWeight: 700, cursor: 'pointer', marginTop: 14 },
  skip:       { width: '100%', height: 48, borderRadius: 24, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 10 },
  note:       { fontSize: 13, color: COLORS.textMid, lineHeight: 1.5, margin: '10px 0 0', textAlign: 'center' },
};
